import {Box, CircularProgress} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, {FunctionComponent, useEffect, useRef, useState} from 'react';
import {ArrowLeft, ExternalLink} from 'react-feather';
import {useNavigate, useSearchParams} from 'react-router';
import {EPaymentRequiredType} from '../../../models/consts';
import {IPaymentRequiredPoppup} from '../../../models/inner-models';
import {IBackupRow} from '../../../views/PlatformPages/types';
import NoBackupsBlock from '../../Blocks/NoBackupsBlock/NoBackupsBlock';
import PaymentRequiredPoppup from '../../Dialogs/PaymentRequiredPoppup/PaymentRequiredPoppup';
import {Button, Typography, WhiteCard} from '../../StyledComponents';
import '../style.scss';
import './styles.scss';
import ActionsTable from '../PlatformActionsTable/ActionsTable';
import {useTranslation} from "react-i18next";
import Platform from '../../../models/platforms-data';
import {BadgesAppWasDeleted, BadgesIFrameMode, BadgesNotionUpd} from "../../Badges/Badges";
import JiraHowTo from "../../../views/PlatformPages/JiraPages/JiraBoardsListView/JiraHowTo/JiraHowTo";
import {getUserProfileName} from "../../../store/actions";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useWebSocket} from "../../../contexts/WebSocketContext";
import {updateActionsState} from '../../../store/reducers/backups';
import {EWebsocketMethods} from "../../../constants";
import {isNotionNeedsReauthSelector, isSubUser} from "../../../store/selectors";
import {iframeNewTabMsg, iniFrame} from "../../../serviceWorker";

const useStyles = makeStyles({
    backupsBlock: {
      width: '100%',
    },
    fullWidthBlock: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    spinner: {
      color: '#319CFF',
    },
  },
);

interface IPlatformActivityPageBlockProps {
  platformName: string;
  loading: boolean;
  downloadZipCallback: (item: IBackupRow) => void;
  restoreItemCallback: (versions: string, name: string, key: string, emails: string[], organizationId?: string) => void;
  rows: IBackupRow[];
  paymentPopup: IPaymentRequiredPoppup;
  paymentRequiredState?: EPaymentRequiredType;
  closeTrialPopupCallback?: ((approved?: (boolean | undefined)) => void);
  appWasDeleted?: boolean
  total: number;
  page: number;
  setPage: (number) => void;
  isLoading: boolean;
  canRestore: boolean;
  isAdmin: boolean;
}

const PlatformActionsPage: FunctionComponent<IPlatformActivityPageBlockProps> = ({
                                                                                   loading,
                                                                                   rows,
                                                                                   downloadZipCallback,
                                                                                   restoreItemCallback,
                                                                                   paymentPopup,
                                                                                   paymentRequiredState,
                                                                                   closeTrialPopupCallback,
                                                                                   platformName, appWasDeleted,
                                                                                   total, page, isAdmin,
                                                                                   setPage, isLoading, canRestore
                                                                                 }: IPlatformActivityPageBlockProps) => {
  const {t: translate} = useTranslation();
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const data = searchParams.get('name')?.split('::') || ''
  const subType = searchParams.get('subType')
  const [isOpenTutorial, setIsOpenTutorial] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [profiles, setProfiles] = useState<Record<string, any>>({});
  const isSubPlatform = platformName === 'gitlab' ? localStorage['gitlabType'] === 'snippet' : platformName === 'github' ? Number(localStorage['githubUser']) === 0 : false
  const {onMessage} = useWebSocket();
  const currentPageRef = useRef(page);
  const isUserSub = useAppSelector(isSubUser);
  const needReAuthNotion = useAppSelector(isNotionNeedsReauthSelector);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resProf = await dispatch(getUserProfileName()).unwrap()
        if (resProf) {
          setProfiles(resProf)
        }
      } catch (e) {
        console.log(e);
      }
    };

    fetchData();

    onMessage((data) => {
      if (![EWebsocketMethods.itemsAddedCountChange, EWebsocketMethods.updateDashboardPlatformItemsAmount].includes(data.method)) {
        dispatch(updateActionsState({...data, page: currentPageRef.current}))
      }
    });

  }, [])

  useEffect(() => {
    currentPageRef.current = page;
  }, [page]);

  return (
    <div id="platform-content">
      {iniFrame() &&
        <BadgesIFrameMode/>
      }
      {platformName === 'notion' && (
        <BadgesNotionUpd key='actionPage' platformName='notion' needAuth={needReAuthNotion}/>
      )}
      {appWasDeleted && platformName === 'github' && (
        <BadgesAppWasDeleted platformName={platformName}/>
      )}
      <WhiteCard>
        <div className="header-block actions-header">
          <div className="actions-header-left" onClick={() => {
            if (platformName === 'github' && (subType === 'gist' || Number(localStorage['githubUser']) === 0 || isSubPlatform)) {
              localStorage['githubUser'] = '0'
              navigate('/platform/dashboard/github')
              return
            }
            if (platformName === 'gitlab') {
              if (subType === 'snippet' || localStorage['gitlabType'] === 'snippet' || isSubPlatform) {
                localStorage['gitlabType'] = 'snippet'
              } else {
                localStorage['gitlabType'] = 'project'
              }
              navigate('/platform/dashboard/gitlab')
              return
            }
            subType && platformName === 'shopify' ?
              navigate(`/platform/dashboard/${platformName}/${subType}`) :
              navigate(`/platform/dashboard/${platformName}`)
          }}>
            <ArrowLeft className="arrow-icon"/>
            <img src={Platform[platformName]?.smallLogo} className="platform-logo" loading="lazy"/>
            <Typography variant="h2" display="inline" className="item-name-header">
              {data[1] && <span className='item-private-type'>{data[1]}</span>}
              {data[0]}
            </Typography>
          </div>
          <div className='actions-header-right'>
            {!isUserSub && (
              <Button variant="outlined" color="primary"
                      onClick={() => {
                        if (iniFrame()) {
                          iframeNewTabMsg()
                        } else if (!iniFrame() && profiles[platformName]) {
                          switch (platformName) {
                            case 'github':
                              window.open(`https://github.com/${profiles.github[Object.keys(profiles.github)[0]]}`, '_blank')
                              break;
                            case 'gitlab':
                              window.open(`https://gitlab.com/dashboard/projects`, '_blank')
                              break;
                            case 'trello':
                              window.open(`https://trello.com/u/${profiles.trello}/boards`, '_blank')
                              break;
                            case 'jira':
                              window.open(`https://${profiles.jira}.atlassian.net/jira/your-work`, '_blank')
                              break;
                            case 'notion':
                            case 'shopify':
                            case 'chatgpt':
                            case 'claude':
                            default:
                              window.open(Platform[platformName].url, '_blank')
                              break
                          }
                        } else {
                          window.open(Platform[platformName].url, '_blank')
                        }
                      }}
              >
                {translate('common.buttons.visit')} {Platform[platformName].title}
                <ExternalLink className="feather-icon-in-button icon-ml"/>
              </Button>
            )}

            {platformName === 'jira' && isAdmin && (
              <Button variant='contained' color='secondary' onClick={() => setIsOpenTutorial(true)} ml={4}>
                How To Restore
              </Button>
            )}
          </div>
        </div>
        <Box sx={{width: '100%'}}>
          <div className="info-block">
            <>
              <div className={classes.backupsBlock}>
                <div id="platform-backups">
                  {loading ?
                    <div className="spinner-wrapper">
                      <CircularProgress className={classes.spinner}/>
                    </div> :
                    <>
                      {!rows.length ?
                        <NoBackupsBlock/> :
                        <div className="backups-table">
                          <ActionsTable
                            rows={rows} downloadZip={downloadZipCallback} name={data[0]}
                            restoreItem={restoreItemCallback} platformName={platformName}
                            type={paymentRequiredState}
                            appWasDeleted={appWasDeleted}
                            total={total}
                            page={page}
                            setPage={setPage}
                            isLoading={isLoading}
                            openTutorial={() => setIsOpenTutorial(true)}
                            canRestore={canRestore}
                            isAdmin={isAdmin}
                          />
                        </div>
                      }
                    </>
                  }
                  <PaymentRequiredPoppup
                    isOpen={paymentPopup.isOpen}
                    closeNotification={closeTrialPopupCallback}
                    type={paymentRequiredState}
                    platformName={paymentPopup.platformName}
                  />
                </div>
              </div>
            </>
          </div>
        </Box>
      </WhiteCard>
      {platformName === 'jira' && (
        <JiraHowTo isOpen={isOpenTutorial} closeNotification={() => setIsOpenTutorial(false)}/>
      )}
    </div>

  );
};

export default (PlatformActionsPage);
